import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { adminApi } from '../../api/admin';

const AdminSecondaryMarket = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [divinityCoachOnly, setDivinityCoachOnly] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const res = await adminApi.getSecondaryMarketSettings();
      setDivinityCoachOnly(!!res?.divinityCoachOnly);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Errore caricamento impostazioni',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggle = async () => {
    if (saving || loading) return;
    const next = !divinityCoachOnly;
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const res = await adminApi.setSecondaryMarketSettings(next);
      setDivinityCoachOnly(!!res?.divinityCoachOnly);
      setMessage({
        type: 'success',
        text: next
          ? 'Filtro attivo: nel mercato secondario del marketplace sono visibili solo card Divinity e Allenatore tra le offerte utente.'
          : 'Filtro disattivato: nel mercato secondario sono visibili tutte le card tra le offerte utente.',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Errore salvataggio',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <ShoppingBag className="w-8 h-8 mr-3 text-blue-400" />
          Mercato secondario
        </h1>
      </div>

      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-emerald-900/40 border border-emerald-600/50 text-emerald-200'
              : 'bg-red-900/40 border border-red-600/50 text-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          )}
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 max-w-2xl space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">Filtro card Divinity e Allenatore</h2>
          <p className="text-sm text-gray-400 mb-6">
            Quando attivo, nel marketplace le sezioni del mercato secondario (Compra subito, Prestiti e aste tra
            utenti) mostrano solo le card Divinity e Allenatore messe in vendita o prestito dai giocatori.
            Le offerte della piattaforma (mercato primario) non sono influenzate.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader className="w-5 h-5 animate-spin" />
              Caricamento...
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg bg-gray-900/60 border border-gray-700">
              <div>
                <div className="text-white font-semibold">
                  Solo Divinity e Allenatore nel mercato secondario
                </div>
                <div className="text-sm text-gray-400 mt-1">
                  Stato: {divinityCoachOnly ? 'Attivo' : 'Disattivo'}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={divinityCoachOnly}
                disabled={saving}
                onClick={handleToggle}
                className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
                  divinityCoachOnly ? 'bg-blue-600' : 'bg-gray-600'
                } ${saving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    divinityCoachOnly ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSecondaryMarket;
